/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Drawer as MuiDrawer, styled, Theme, CSSObject } from '@mui/material'
import { DrawerToggleButton } from '@components/systems/drawer/DrawerToggleButton'
import { DrawerHeader } from '@components/systems/drawer/DrawerHeader'
import { DrawerMenuList } from '@components/systems/drawerMenu/DrawerMenuList'
import { DrawerBottomView } from '@components/systems/drawer/DrawerBottomView'

import { useAppDispatch, useAppSelector, configActions } from '@redux/index'

export const Drawer = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const backgroundColor: string = useAppSelector(state => state.config.backgroundColor)
	const mode: 'main' | 'admin' = useAppSelector(state => state.config.mode)
	const drawerWidth: number = useAppSelector(state => state.config.drawerWidth)
	const isOpenDrawer: boolean = useAppSelector(state => state.config.isOpenDrawer)
	const drawerScrollTop: number = useAppSelector(state => state.config.drawerScrollTop)

	const drawerScrollRef = React.useRef<any | null>(null)
	const refDrawerScrollTop: any = React.useMemo(() => {
		return drawerScrollRef.current !== null && drawerScrollRef.current.scrollTop
			? drawerScrollRef.current.scrollTop
			: null
	}, [drawerScrollRef.current])

	React.useEffect(() => {
		if (drawerScrollRef.current.scrollTop !== undefined) {
			drawerScrollRef.current.scrollTop = drawerScrollTop
		}
	}, [drawerScrollTop])

	React.useEffect(() => {
		if (drawerScrollRef.current !== null) {
			const div: any = drawerScrollRef.current

			if (refDrawerScrollTop) {
				div.scrollTop = refDrawerScrollTop
				let scrollTimer: ReturnType<typeof setTimeout>
				div.addEventListener('scroll', () => {
					if (scrollTimer !== undefined) {
						clearTimeout(scrollTimer)
					}

					scrollTimer = setTimeout(function () {
						reduxDispatch(configActions.DRAWER_SCROLL_TOP(refDrawerScrollTop))
					}, 150)
				})
			}
		}
	}, [refDrawerScrollTop, drawerScrollRef.current, drawerScrollTop])

	const openedMixin = React.useCallback(
		(theme: Theme): CSSObject => ({
			width: drawerWidth,
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
			overflowX: 'inherit',
			overflow: 'inherit',
		}),
		[drawerWidth],
	)

	const closedMixin = React.useCallback(
		(theme: Theme): CSSObject => ({
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen,
			}),
			overflowX: 'inherit',
			overflow: 'inherit',
			width: `calc(${theme.spacing(7)} + 1px)`,
			[theme.breakpoints.up('sm')]: {
				width: `calc(${theme.spacing(8)} + 1px)`,
			},
		}),
		[],
	)

	const DrawerComponent = styled(MuiDrawer, {
		shouldForwardProp: prop => prop !== 'open',
	})(({ theme, open }) => ({
		width: drawerWidth,
		flexShrink: 0,
		whiteSpace: 'nowrap',
		backgroundColor: backgroundColor,
		boxSizing: 'border-box',
		...(open && {
			...openedMixin(theme),
			'& .MuiDrawer-paper': openedMixin(theme),
		}),
		...(!open && {
			...closedMixin(theme),
			'& .MuiDrawer-paper': closedMixin(theme),
		}),
	}))

	return (
		<DrawerComponent variant="permanent" anchor="left" open={isOpenDrawer} css={style}>
			<DrawerToggleButton />
			<DrawerHeader />

			<View
				className="menuListView"
				css={
					mode === 'main'
						? { maxHeight: 'calc(100vh - 9.7rem)' }
						: { maxHeight: 'calc(100vh - 12.5rem)' }
				}
				ref={drawerScrollRef}
			>
				<DrawerMenuList />
			</View>

			<DrawerBottomView />
		</DrawerComponent>
	)
}

const style = css({
	zIndex: 7,
	overflow: 'inherit',

	'.menuListView': {
		overflow: 'auto',
		marginTop: '2rem',
	},

	'.bottomView': {
		position: 'absolute',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		minHeight: '3.9rem',
		bottom: 0,
	},
})
